import {setUser} from '@store/auth'
import {Alert, Button, Form, Input, Space, message, Typography, Divider, Table, Popconfirm, Tag, Tooltip} from 'antd'
import PageHeader from '@components/page-header'
import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {useFetchGet, useFetchPost, useDelete, Params} from 'helpers/api'
import {CopyOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons'

/**
 * User Profile Component - Token Management Integration (Revised Design)
 *
 * FUNCTIONALITY: Full Token Management (Create, List, Revoke)
 *
 * CONDITIONAL RENDERING:
 * - Always shows token management section (no conditions)
 * - This is the user's own profile - full self-service capabilities
 *
 * AVAILABLE ACTIONS:
 * ┌──────────────────┬─────────────────────────────────────────────┐
 * │ Action           │ Description                                 │
 * ├──────────────────┼─────────────────────────────────────────────┤
 * │ Create Token     │ Generate new API access token with name    │
 * │ View Tokens      │ List all tokens (active and revoked)       │
 * │ Revoke Token     │ Invalidate a token permanently             │
 * │ Copy Token       │ One-time copy of newly created token       │
 * └──────────────────┴─────────────────────────────────────────────┘
 *
 * Note: Admin oversight of other users' tokens is in User Editor Modal
 */
const Profile = ({onClose, onSuccess = () => {}}) => {
  const user = useSelector(state => state.auth.user)
  const token = useSelector(state => state.auth.token)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Token management state
  const [tokens, setTokens] = useState([])
  const [tokensLoading, setTokensLoading] = useState(false)
  const [tokenError, setTokenError] = useState(null)
  const [showNewToken, setShowNewToken] = useState(false)
  const [newTokenValue, setNewTokenValue] = useState('')

  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Optimized API integration following DTI patterns
  const fetchTokens = useFetchGet('auth/tokens')
  const createToken = useFetchPost('auth/tokens')
  const deleteToken = useDelete('auth/tokens')

  /**
   * Conditional Rendering Logic for Token Management
   *
   * User Profile shows FULL TOKEN MANAGEMENT (create, list, revoke):
   * - This is where users manage their own access tokens
   * - Always shows token management section (no conditions - user's own profile)
   * - Full capabilities: Create new tokens, view all tokens, revoke tokens
   *
   * Note: This is the primary interface for users to manage their API tokens
   *       Admin oversight is handled in the User Editor Modal
   */

  const handleClose = () => {
    setError(null)
    setTokenError(null)
    setIsLoading(false)
    setTokensLoading(false)
    setShowNewToken(false)
    setNewTokenValue('')
    navigate(-1)
  }

  const onFinish = values => {
    setIsLoading(true)
    const request = {
      method: 'PUT',
      body: JSON.stringify(values),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        authorization: 'Bearer ' + token
      },
      credentials: 'include'
    }
    fetch(process.env.REACT_APP_BACKEND_URL + `/users/me`, request)
      .then(async response => {
        if (response?.status === 400) {
          return false
        }
        return response.json()
      })
      .then(res => {
        setIsLoading(false)
        if (res) {
          onSuccess()
          handleClose()
          message.success('Successfully updated user!')
          dispatch(setUser({user: res}))
        } else {
          setError({status: 400})
        }
      })
  }

  // Optimized token management functions with comprehensive error handling following DTI patterns
  const loadTokens = () => {
    setTokensLoading(true)
    setTokenError(null)

    // Use Params for query parameters (status filter, pagination, etc.)
    const params = Params({status: 'all'}) // Get both active and revoked tokens

    fetchTokens(params, response => {
      setTokensLoading(false)
      if (response && response.tokens) {
        setTokens(response.tokens)
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        setTokens(response)
      } else {
        setTokens([])
        // Only show error if response is explicitly falsy (connection issues)
        if (response === false || response === undefined) {
          const errorMessage = 'Failed to load access tokens. Please refresh the page and try again.'
          setTokenError(errorMessage)
        }
      }
    })
  }

  const handleCreateToken = values => {
    setTokensLoading(true)
    setTokenError(null)
    createToken(
      values,
      response => {
        setTokensLoading(false)
        if (response && response.token) {
          setNewTokenValue(response.token)
          setShowNewToken(true)
          loadTokens() // Refresh the list
          message.success(
            values?.name ? `Access token "${values.name}" created successfully!` : 'Access token created successfully!'
          )
        } else {
          const errorMessage =
            'Failed to create access token. The server did not return a valid token. Please try again.'
          setTokenError(errorMessage)
        }
      },
      error => {
        setTokensLoading(false)
        const errorMessage =
          error?.message ||
          error?.statusText ||
          'Failed to create access token. Please check your connection and try again.'
        setTokenError(errorMessage)
        console.error('Token creation error:', error)
      }
    )
  }

  const handleRevokeToken = tokenId => {
    setTokensLoading(true)
    setTokenError(null)
    deleteToken(
      tokenId,
      response => {
        setTokensLoading(false)
        if (response) {
          loadTokens() // Refresh the list
          message.success(
            'Access token revoked successfully. Applications using this token can no longer access the API.'
          )
        } else {
          const errorMessage =
            'Failed to revoke access token. The token may have already been revoked. Please try again.'
          setTokenError(errorMessage)
        }
      },
      error => {
        setTokensLoading(false)
        const errorMessage =
          error?.message ||
          error?.statusText ||
          'Failed to revoke access token. Please check your connection and try again.'
        setTokenError(errorMessage)
        console.error('Token revocation error:', error)
      }
    )
  }

  const copyToClipboard = text => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Token copied to clipboard!')
      })
      .catch(() => {
        message.error('Failed to copy token')
      })
  }

  // Load tokens when component mounts
  useEffect(() => {
    loadTokens()
  }, [])

  // Helper function to format dates with time
  const formatDateTime = dateString => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Helper function to get relative time
  const getRelativeTime = dateString => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return formatDateTime(dateString)
  }

  // Enhanced token table columns with improved display
  const tokenColumns = [
    {
      title: 'Token Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (name, record) => (
        <div>
          <div style={{fontWeight: 500}}>{name || <em style={{color: '#999'}}>Unnamed Token</em>}</div>
          <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
            <Tag color="blue" style={{fontSize: '11px'}}>
              {record.prefix || 'N/A'}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: '20%',
      render: created => (
        <div>
          <div>{formatDateTime(created)}</div>
          <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(created)}</div>
        </div>
      )
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_used',
      key: 'last_used',
      width: '20%',
      render: lastUsed => (
        <div>
          <div>{lastUsed ? formatDateTime(lastUsed) : <span style={{color: '#999'}}>Never used</span>}</div>
          {lastUsed && (
            <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(lastUsed)}</div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'revoked',
      key: 'status',
      width: '15%',
      render: (revoked, record) => (
        <div>
          <Tag color={revoked ? 'red' : 'green'} style={{marginBottom: '4px'}}>
            {revoked ? 'Revoked' : 'Active'}
          </Tag>
          {revoked && <div style={{fontSize: '11px', color: '#999'}}>{getRelativeTime(revoked)}</div>}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      align: 'right',
      render: (_, record) => (
        <Space>
          {!record.revoked && (
            <Tooltip title="Immediately invalidate this token - any applications using it will lose access">
              <Popconfirm
                title="Revoke Access Token"
                description={
                  <div>
                    <p style={{marginBottom: 8}}>
                      Are you sure you want to revoke <strong>{record.name || 'this token'}</strong>?
                    </p>
                    <p style={{marginBottom: 4, color: '#ff4d4f'}}>
                      <strong>This action cannot be undone.</strong>
                    </p>
                    <p style={{marginBottom: 0, fontSize: '12px', color: '#666'}}>
                      Applications using this token will immediately lose API access.
                    </p>
                  </div>
                }
                onConfirm={() => handleRevokeToken(record.id)}
                okText="Yes, Revoke Token"
                cancelText="Cancel"
                okButtonProps={{danger: true}}
                placement="topRight"
              >
                <Button type="text" danger icon={<DeleteOutlined />} size="small">
                  Revoke
                </Button>
              </Popconfirm>
            </Tooltip>
          )}
          {record.revoked && (
            <Tooltip title={`Revoked ${getRelativeTime(record.revoked)}`}>
              <span style={{color: '#999'}}>—</span>
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader
        style={{marginLeft: '8vw'}}
        ghost={false}
        onBack={() => window.history.back()}
        title="User Profile"
        subTitle=""
        extra={[]}
      />
      <Form
        layout="horizontal"
        labelCol={{span: 3}}
        labelWrap={true}
        wrapperCol={{span: 10, offset: 0}}
        initialValues={user}
        onFinish={onFinish}
        autoComplete="off"
        key={user ? user.id || -1 : 0}
      >
        {error && (
          <Alert
            message={error?.status === 400 ? 'User with this email already exists!' : 'Something went wrong!'}
            type="error"
            showIcon
            style={{marginBottom: 24}}
          />
        )}

        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: 'Name is required!'
            },
            {
              min: 2,
              message: 'Must be at least 2 characters!'
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            {
              required: true,
              message: 'Email is required!'
            },
            {
              type: 'email',
              message: 'Email is not a valid email!'
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item wrapperCol={{span: 10, offset: 3}}>
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Access Token Management Section */}
      <div style={{marginLeft: '8vw', marginRight: '8vw'}}>
        <Divider orientation="left">Access Token Management</Divider>

        {/* Enhanced Security Notice */}
        <Alert
          message="Personal Access Tokens"
          description={
            <div>
              <p style={{marginBottom: 8}}>
                Personal access tokens function like passwords for API authentication. Use them to access the DTI API
                without requiring your login credentials.
              </p>
              <p style={{marginBottom: 0}}>
                <strong>Security Best Practices:</strong>
              </p>
              <ul style={{marginTop: 4, marginBottom: 0, paddingLeft: 20}}>
                <li>Treat tokens like passwords - keep them secret</li>
                <li>Never share tokens in emails, chat messages, or public repositories</li>
                <li>Create separate tokens for different applications or purposes</li>
                <li>Revoke tokens immediately if compromised or no longer needed</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{marginBottom: 16}}
        />

        {tokenError && (
          <Alert
            message={tokenError}
            type="error"
            showIcon
            style={{marginBottom: 16}}
            closable
            onClose={() => setTokenError(null)}
          />
        )}

        {/* Enhanced Token Creation */}
        <div
          style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 4
          }}
        >
          <Typography.Title level={5} style={{marginTop: 0, marginBottom: 8}}>
            Create New Token
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{display: 'block', marginBottom: 16, fontSize: '13px'}}
            id="token-name-description"
          >
            Give your token a descriptive name to help identify its purpose. The token will be displayed once after
            creation.
          </Typography.Text>
          <Form layout="inline" onFinish={handleCreateToken}>
            <Form.Item
              name="name"
              rules={[
                {
                  max: 255,
                  message: 'Token name must not exceed 255 characters'
                }
              ]}
              style={{marginBottom: 0}}
            >
              <Input
                placeholder="e.g., Mobile App, CI/CD Pipeline"
                style={{width: 250}}
                disabled={tokensLoading}
                maxLength={255}
                aria-label="Token name"
                aria-describedby="token-name-description"
              />
            </Form.Item>
            <Form.Item style={{marginBottom: 0}}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={tokensLoading}
                aria-label="Generate new access token"
              >
                Generate Token
              </Button>
            </Form.Item>
          </Form>
        </div>

        {/* Enhanced New Token Display */}
        {showNewToken && newTokenValue && (
          <Alert
            message={
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <span>New Access Token Created Successfully</span>
                <Button
                  type="primary"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newTokenValue)}
                  style={{marginLeft: 8}}
                  aria-label="Copy token to clipboard"
                >
                  Copy Token
                </Button>
              </div>
            }
            description={
              <div>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    marginBottom: 12,
                    marginTop: 8
                  }}
                >
                  <code
                    style={{
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      color: '#262626'
                    }}
                  >
                    {newTokenValue}
                  </code>
                </div>
                <Alert
                  message="Security Warning"
                  description={
                    <div>
                      <p style={{marginBottom: 4}}>
                        <strong>⚠️ This token will only be shown once and cannot be retrieved again.</strong>
                      </p>
                      <p style={{marginBottom: 0}}>
                        Make sure to copy and store it securely now. If you lose this token, you'll need to generate a
                        new one.
                      </p>
                    </div>
                  }
                  type="warning"
                  showIcon
                  style={{marginBottom: 0}}
                  banner
                />
              </div>
            }
            type="success"
            showIcon
            style={{marginBottom: 16}}
            closable
            closeText="I've copied the token"
            onClose={() => {
              setShowNewToken(false)
              setNewTokenValue('')
            }}
          />
        )}

        {/* Existing Tokens Table with responsive design */}
        <Table
          columns={tokenColumns}
          dataSource={tokens}
          rowKey="id"
          loading={tokensLoading}
          pagination={false}
          size="small"
          locale={{
            emptyText: 'No access tokens found'
          }}
          rowClassName={record => (record.revoked ? 'revoked-token-row' : '')}
          scroll={{x: 800}}
          aria-label="Access tokens table"
        />
      </div>
    </div>
  )
}

export default Profile
